<template>
  <b-overlay
    :show="loading"
    spinner-variant="primary"
    spinner-type="grow"
    spinner-small
    rounded="sm"
  >
    <!-- Breadcrumb -->
    <app-breadcrumb>
      <template #contentright>
        <b-button
          v-if="$route.params.id !== undefined"
          variant="outline-primary"
          :to="{ name: 'permissions-add' }"
        >
          <span class="text-nowrap">
            <feather-icon icon="PlusCircleIcon" /> {{ $t('Add New') }}
          </span>
        </b-button>
        <span v-else />
      </template>
    </app-breadcrumb>
    <b-row>
      <b-col md="6">
        <b-card aria-hidden="true">
          <!-- Alert: No item found -->
          <b-alert
            variant="danger"
            :show="errorMessage !== ''"
          >
            <h4 class="alert-heading">
              {{ errorMessage }}
            </h4>

          </b-alert>

          <!-- BODY -->
          <validation-observer
            #default="{ handleSubmit }"
            ref="refFormObserver"
          >
            <!-- Form -->
            <b-form
              class=""
              @submit.prevent="handleSubmit(onSubmit)"
            >
              <!-- Name -->
              <validation-provider
                #default="validationContext"
                :name="$t('Name')"
                rules="required|alpha-num-dot-or-dash|max:150"
              >
                <b-form-group label-for="name">
                  <template #label>
                    <label class="d-block font-weight-bolder">{{ $t('Name') }} <span class="text-danger">*</span></label>
                  </template>
                  <b-form-input
                    id="name"
                    v-model="itemData.name"
                    :state="getValidationState(validationContext)"
                    trim
                  />

                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <!-- Description -->
              <validation-provider
                #default="validationContext"
                :name="$t('Display Name')"
                rules="required|max:150"
              >
                <b-form-group
                  :label="$t('Display Name')"
                  label-for="description"
                >
                  <template #label>
                    <label class="d-block font-weight-bolder">{{ $t('Display Name') }} <span class="text-danger">*</span></label>
                  </template>
                  <b-form-input
                    id="description"
                    v-model="itemData.description"
                    :state="getValidationState(validationContext)"
                  />

                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <!-- Permission Module -->
              <validation-provider
                #default="validationContext"
                :name="$t('Module')"
                rules="required"
              >
                <b-form-group label-for="module_id">
                  <template #label>
                    <label class="d-block font-weight-bolder">{{ $t('Module') }} <span class="text-danger">*</span></label>
                  </template>
                  <v-select
                    v-model="itemData.module_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="moduleOptions"
                    :reduce="(val) => val.id"
                    label="description"
                    :state="getValidationState(validationContext)"
                    input-id="module_id"
                  />
                  <b-form-invalid-feedback :state="getValidationState(validationContext)">
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <b-form-group>
                <b-form-checkbox
                  id="assign-state"
                  v-model="assignState"
                  name="assign-state"
                  value="1"
                  unchecked-value="0"
                >
                  Gán quyền vào trạng thái tin bài
                </b-form-checkbox>
              </b-form-group>

              <b-form-group v-show="assignState">
                <template #label>
                  <label class="d-block font-weight-bolder">{{ $t('State') }}</label>
                </template>
                <v-select
                  v-model="itemData.state"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="stateOptions"
                  :reduce="(val) => val.id"
                  label="name"
                />
              </b-form-group>

              <!-- Form Actions -->
              <div class="d-flex mt-2">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mr-2"
                  type="submit"
                >
                  {{ $t('Save') }}
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </b-card>
      </b-col>
    </b-row>
  </b-overlay>
</template>

<script>
import {
  BRow, BCol, BCard, BAlert, BOverlay, BForm, BFormGroup, BButton, BFormInput, BFormInvalidFeedback, BFormCheckbox,
} from 'bootstrap-vue'
import {
  ref, watch, computed,
} from '@vue/composition-api'
import store from '@/store'
import { useRouter, showToast } from '@core/utils/utils';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import {
  required, alphaNumDot, alphaNumDotOrDash, max,
} from '@validations';
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import AppBreadcrumb from '@core/layouts/components/AppBreadcrumb.vue'
import vSelect from 'vue-select'
import usePermissions from './usePermissions'

export default {
  components: {
    AppBreadcrumb,
    BRow,
    BCol,
    BCard,
    BAlert,
    BOverlay,
    BForm,
    BFormGroup,
    BButton,
    BFormInput,
    BFormCheckbox,
    BFormInvalidFeedback,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      required,
      alphaNumDot,
      alphaNumDotOrDash,
      max,
    }
  },
  computed: {

  },

  setup(props, context) {
    const { route, router } = useRouter();
    const id = ref(route.value.params.id !== undefined ? route.value.params.id : 0);
    const layout = ref(route.value.name !== 'permissions-edit' ? 'add' : 'edit');
    const loading = ref(!!id.value)
    const assignState = ref(false)
    const stateOptions = ref([])
    const userId = ref(null)
    const departmentsData = ref([])
    const positionsData = ref([])
    const errorMessage = ref('')
    const permissions = ref([])
    const defaultItemData = {
      name: null,
      description: null,
      module_id: null,
    }
    const itemData = ref(defaultItemData)

    const resetItemData = () => {
      itemData.value = JSON.parse(JSON.stringify(defaultItemData))
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetItemData)

    const {
      fetchModules,
      moduleOptions,
    } = usePermissions()

    fetchModules();

    const onSubmit = () => {
      errorMessage.value = '';
      loading.value = true;

      if (layout.value === 'edit') {
        store.dispatch('permissions/updatePermission', { id: id.value, permissionData: itemData.value })
          .then((response) => {
            loading.value = false
            if (response.status === 200) {
              if (response.data.code === 200) {
                showToast(response.data.message, { variant: 'success' });
                router.push({ name: 'permissions-list' })
              } else {
                showToast(response.data.message)
                errorMessage.value = response.data.message;
              }
            }
          })
      } else {
        store.dispatch('permissions/addPermission', itemData.value)
          .then((response) => {
            loading.value = false
            if (response.status === 200) {
              if (response.data.code === 200) {
                showToast(response.data.message, { variant: 'success' });
                // resetForm()
                router.push({ name: 'permissions-list' })
              } else {
                showToast(response.data.message)
                errorMessage.value = response.data.message;
              }
            }
          })
      }
    }

    const fetchStates = () => {
      store.dispatch('state/fetchAll', { type: 'all' })
        .then(response => {
          if (response.status === 200) {
            stateOptions.value = response.data.data
          }
        })
    }

    const fetchData = () => {
      loading.value = true;
      store.dispatch('permissions/fetchPermission', { id: id.value })
        .then(response => {
          if (response.status === 200) {
            itemData.value = response.data.data
            loading.value = false
            errorMessage.value = '';
            if (itemData.value.state !== undefined && itemData.value.state) {
              assignState.value = 1;
            }
          } else {
            errorMessage.value = response.message
            loading.value = false
          }
        })
    }
    if (id.value && layout.value === 'edit') {
      fetchStates();
      fetchData();
    }

    watch(
      () => context.root.$route,
      (to) => {
        if (to.name === 'permissions-add') {
          resetItemData();
        } else {
          fetchData();
        }
      },
    )
    watch(assignState, (newValue, oldValue) => {
      if (newValue && !stateOptions.value.length) {
        fetchStates();
      }
    });
    return {
      id,
      layout,
      errorMessage,
      loading,
      itemData,
      departmentsData,
      positionsData,
      userId,
      permissions,
      refFormObserver,
      getValidationState,
      resetForm,
      resetItemData,
      onSubmit,
      moduleOptions,
      assignState,
      stateOptions,
    }
  },

}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
